/**
 * useTeamMembers -- Distributor Team & Permissions members data layer.
 *
 * Card: Team & Permissions: real members table + full kebab
 * (https://trello.com/c/IlgSKtZl). PR-1/4.
 *
 * The Team & Permissions members table now sources from real member
 * ACCOUNTS (impersonation-aware) via GET /distributor/teamMembers, NOT
 * from the legacy Stripe-subscription roster. The endpoint resolves the
 * tenant server-side from effective_distributor_id(), so this composable
 * does NOT pass a distributor id -- the request is always correctly scoped
 * (including under SA impersonation) with no IDOR surface.
 *
 * Member objects (see the backend get_team_members docstring):
 *   { id, name, email, role, scope, last_active, status, stores,
 *     reset_code, is_me }
 *
 * Mirrors useTeamSubscription's testability contract: axios is injected so
 * the composable mounts without a live backend; it exposes reactive
 * `members` + `loading` + `load()` plus the wired kebab action handlers.
 */
import { reactive, toRefs } from "vue";
import { notifyTeamChanged } from "@/stores/sidebarRefresh";

export function useTeamMembers({ axios } = {}) {
    const state = reactive({
        members: [],
        loading: false,
        // Transient success/error notice for kebab actions (reset password,
        // resend invite, copy link). The panel reads this to flash a toast.
        notice: null,
    });

    function setNotice(kind, message) {
        state.notice = { kind, message };
    }

    async function load() {
        state.loading = true;
        try {
            const response = await axios.get("/distributor/teamMembers");
            state.members = Array.isArray(response && response.data)
                ? response.data
                : [];
        } catch (err) {
            state.members = [];
            setNotice("error", "Could not load team members.");
        } finally {
            state.loading = false;
        }
    }

    // --- Wired kebab actions ------------------------------------------------

    // Reset password (active rows): trigger a password-reset email. The
    // /auth/sendResetPasswordToken route takes { email } and needs no auth.
    async function resetPassword(member) {
        try {
            await axios.post("/auth/sendResetPasswordToken", {
                email: member.email,
            });
            setNotice("success", `Password-reset link sent to ${member.email}.`);
            return true;
        } catch (err) {
            setNotice("error", "Could not send the password-reset link.");
            return false;
        }
    }

    // Resend invite (pending rows): resends ALL pending invitations for the
    // effective tenant (the backend route is impersonation-aware and does
    // not take a single email).
    async function resendInvite() {
        try {
            await axios.get("/distributor/resendInvitationEmails");
            setNotice("success", "Invitation email resent.");
            return true;
        } catch (err) {
            setNotice("error", "Could not resend the invitation.");
            return false;
        }
    }

    // Copy invite link (pending rows): build the signup URL the invite email
    // itself uses -- base + "/new-user/" + reset_code (see
    // api/src/plugins/mail.py signup_email_to_teammates_with_token, which
    // points at the frontend /new-user/:id route) -- and copy to clipboard.
    function inviteLinkFor(member) {
        if (!member || !member.reset_code) {
            return null;
        }
        const origin =
            typeof window !== "undefined" && window.location
                ? window.location.origin
                : "";
        return `${origin}/new-user/${member.reset_code}`;
    }

    async function copyInviteLink(member) {
        const url = inviteLinkFor(member);
        if (!url) {
            setNotice("error", "No invite link available for this teammate.");
            return false;
        }
        try {
            if (
                typeof navigator !== "undefined" &&
                navigator.clipboard &&
                navigator.clipboard.writeText
            ) {
                await navigator.clipboard.writeText(url);
            }
            setNotice("success", "Invite link copied to clipboard.");
            return true;
        } catch (err) {
            setNotice("error", "Could not copy the invite link.");
            return false;
        }
    }

    // Change role (active member): promote a Builder -> Owner or demote an
    // Owner -> Builder via POST /distributor/teamMembers/<id>/role. The
    // endpoint resolves the tenant server-side from effective_distributor_id()
    // (impersonation-aware) and is gated on the Owner-only
    // distributor.team.invite capability, so this composable passes ONLY the
    // target account id + the new role slug -- never a distributor id. On
    // success the caller re-loads the roster so the row's chip updates.
    //
    // Returns true on success, false otherwise (the panel reads `notice` to
    // surface the outcome). The last-owner-demote guard returns a clean 409
    // from the backend; we map it to a clear message rather than faking a
    // success.
    async function changeRole(member, role) {
        if (!member || member.id == null) {
            setNotice("error", "Cannot change this member's role.");
            return false;
        }
        if (role !== "owner" && role !== "builder") {
            setNotice("error", "Unknown role.");
            return false;
        }
        try {
            await axios.post(`/distributor/teamMembers/${member.id}/role`, {
                role,
            });
            setNotice(
                "success",
                role === "owner"
                    ? `${member.name || member.email} is now an Owner.`
                    : `${member.name || member.email} is now a Builder.`
            );
            // Refresh the roster so the role chip + scope text reflect the
            // change (and the kebab item set for that row updates).
            await load();
            // The TEAM sidebar count is by-role, so a role change moves the
            // "N owners . M builders" tally -> refresh it. Interim seam pending
            // store-ification (https://trello.com/c/g9K52FsA).
            notifyTeamChanged();
            return true;
        } catch (err) {
            const status = err && err.response ? err.response.status : null;
            if (status === 409) {
                setNotice(
                    "error",
                    "Cannot demote the last Owner. Transfer ownership to " +
                        "another member first."
                );
            } else {
                setNotice("error", "Could not change the member's role.");
            }
            return false;
        }
    }

    // Extract the server's inline error detail from a JSON:API-style error
    // envelope (``errors[0].detail``) or a plain ``message``/``detail`` body,
    // falling back to a generic string. Used so a duplicate-email 422 surfaces
    // the backend's message (the "already associated with another account"
    // detail) in the dialog rather than being swallowed behind a generic
    // notice.
    function serverErrorDetail(err, fallback) {
        const data = err && err.response ? err.response.data : null;
        if (data) {
            if (
                Array.isArray(data.errors) &&
                data.errors.length &&
                data.errors[0] &&
                data.errors[0].detail
            ) {
                return String(data.errors[0].detail);
            }
            if (data.detail) return String(data.detail);
            if (data.message) return String(data.message);
        }
        return fallback;
    }

    // Edit team member (active rows): first/last name, EMAIL, and role in ONE
    // coherent write via POST /distributor/teamMembers/<id>/profile. Email is
    // the LOGIN identity, so this cannot be a plain SAFRS name PATCH -- the
    // endpoint revalidates email uniqueness, syncs the username so login stays
    // coherent, applies the role change, and re-sends any still-open
    // invitation to the corrected address, all server-side and atomically.
    // The tenant + capability gate live on the endpoint (Owner-only); this
    // composable passes ONLY the target account id + edited fields.
    //
    // Returns ``{ ok: true }`` on success or ``{ ok: false, message }`` with
    // the server's inline detail (e.g. a duplicate-email 422) so the caller
    // can render it in the dialog without closing it.
    async function editMember(member, { firstName, lastName, email, role } = {}) {
        if (!member || member.id == null) {
            return { ok: false, message: "Cannot edit this member." };
        }
        const payload = {
            first_name: firstName,
            last_name: lastName,
            email,
        };
        // Only send role when supplied so a name/email-only edit does not
        // force a role write.
        if (role) {
            payload.role = role;
        }
        try {
            await axios.post(
                `/distributor/teamMembers/${member.id}/profile`,
                payload
            );
            // Reflect the change locally so the row updates without a reload.
            const parts = [firstName, lastName].filter(
                (p) => p && String(p).trim()
            );
            if (parts.length) {
                member.name = parts.map((p) => String(p).trim()).join(" ");
            }
            if (email) member.email = email;
            if (role) member.role = role;
            setNotice("success", "Team member updated.");
            // editMember can change the member's role, which moves the by-role
            // TEAM sidebar tally; a name/email-only edit does not, but a
            // redundant refetch is harmless, so emit unconditionally on success.
            // Interim seam pending store-ification (https://trello.com/c/g9K52FsA).
            notifyTeamChanged();
            return { ok: true };
        } catch (err) {
            return {
                ok: false,
                message: serverErrorDetail(
                    err,
                    "Could not update the team member."
                ),
            };
        }
    }

    // Deactivate (active rows) / revoke the invite (pending rows) via POST
    // /subscription/deleteTeammate/. Deactivation removes the PERSON only:
    // the server reassigns every live store they created to the acting Owner
    // and returns those stores so the caller can offer to move each one on.
    // The tenant + Owner-only capability gate live on the endpoint; no
    // distributor id is sent (the server resolves the effective tenant).
    //
    // Returns ``{ ok: true, reassignedStores, reassignedTo }`` or
    // ``{ ok: false, message }`` with the server's refusal (cannot deactivate
    // yourself / the last active Owner) so the dialog can show it.
    async function deactivateMember(member) {
        if (!member) {
            return { ok: false, message: "Cannot deactivate this member." };
        }
        const pending = member.status === "pending";
        try {
            const response = await axios.post("/subscription/deleteTeammate/", {
                source: "distributor",
                teammate: {
                    id: member.id,
                    email: member.email,
                    status: pending ? "invited" : "active",
                },
            });
            const data = (response && response.data) || {};
            setNotice(
                "success",
                pending
                    ? `Invitation for ${member.email} revoked.`
                    : `${member.name || member.email} was deactivated.`
            );
            // The TEAM sidebar tally counts members; one just left.
            notifyTeamChanged();
            return {
                ok: true,
                reassignedStores: Array.isArray(data.reassigned_stores)
                    ? data.reassigned_stores
                    : [],
                reassignedTo: data.reassigned_to || null,
            };
        } catch (err) {
            const data = err && err.response ? err.response.data : null;
            return {
                ok: false,
                message:
                    (data && data.error) ||
                    serverErrorDetail(
                        err,
                        pending
                            ? "Could not revoke the invitation."
                            : "Could not deactivate this member."
                    ),
            };
        }
    }

    return {
        ...toRefs(state),
        load,
        resetPassword,
        resendInvite,
        inviteLinkFor,
        copyInviteLink,
        changeRole,
        editMember,
        deactivateMember,
    };
}
